<template>
  <div class="kv-ve__group">
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-common-align") }}</label>
      <VeAlignPicker
        v-model="alignProxy"
        :label="$_('visualEditor-emailEditor-common-align')"
        :leftTitle="$_('visualEditor-emailEditor-common-left')"
        :centerTitle="$_('visualEditor-emailEditor-common-center')"
        :rightTitle="$_('visualEditor-emailEditor-common-right')"
        :allowEmpty="true"
      />
    </div>

    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-common-display") }}</label>
      <VeDisplayModePicker
        v-model="displayProxy"
        :label="$_('visualEditor-emailEditor-common-display')"
        :block-title="$_('visualEditor-emailEditor-common-displayBlock')"
        :inline-title="$_('visualEditor-emailEditor-common-displayInline')"
      />
    </div>

    <div v-if="displayProxy === 'inline-block'" class="form-group">
      <div style="display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:8px;">
        <div style="display:flex; align-items:baseline; gap:6px;">
          <label style="margin:0;">{{ $_("visualEditor-emailEditor-common-width") }}</label>
          <VeWidthUnitToggle
            v-model="widthModeProxy"
            :label="$_('visualEditor-emailEditor-common-width')"
            :pctTitle="$_('visualEditor-emailEditor-common-unitPercent')"
            :pxTitle="$_('visualEditor-emailEditor-common-unitPx')"
          />
        </div>
        <div style="display:flex; align-items:center; gap:8px;">
          <input
            v-if="widthModeProxy === 'pct'"
            class="form-control"
            type="number"
            v-model.number="widthPctProxy"
            min="10"
            max="100"
            style="width: 88px; height: 34px; padding: 6px 10px; font-size: 12px; font-variant-numeric: tabular-nums;"
          />
          <input
            v-else
            class="form-control"
            type="number"
            v-model.number="widthPxProxy"
            min="0"
            max="600"
            style="width: 88px; height: 34px; padding: 6px 10px; font-size: 12px; font-variant-numeric: tabular-nums;"
          />
          <div style="font-size:12px; font-variant-numeric: tabular-nums; color:#667085;">
            {{ widthModeProxy === "pct" ? $_("visualEditor-emailEditor-common-unitPercent") : $_("visualEditor-emailEditor-common-unitPx") }}
          </div>
        </div>
      </div>
      <VeWidthSlider
        v-if="widthModeProxy === 'pct'"
        v-model="widthPctProxy"
        :label="$_('visualEditor-emailEditor-common-width')"
        :min="10"
        :max="100"
        :step="1"
        unit="%"
        :showHeader="false"
      />
      <VeWidthSlider
        v-else
        v-model="widthPxProxy"
        :label="$_('visualEditor-emailEditor-common-widthPx')"
        :min="0"
        :max="600"
        :step="10"
        unit="px"
        :showHeader="false"
      />
    </div>

    <VeBlockMarginsSettings :block="block" />

  </div>
</template>

<script>
import VeAlignPicker from "../../components/VeAlignPicker.vue";
import VeBlockMarginsSettings from "../VeBlockMarginsSettings.vue";
import VeDisplayModePicker from "../../components/VeDisplayModePicker.vue";
import VeWidthSlider from "../../components/VeWidthSlider.vue";
import VeWidthUnitToggle from "../../components/VeWidthUnitToggle.vue";

export default {
  name: "VeTextBlockSettings",
  components: { VeAlignPicker, VeBlockMarginsSettings, VeDisplayModePicker, VeWidthSlider, VeWidthUnitToggle },
  props: {
    block: { type: Object, required: true },
    paletteLabel: { type: String, required: true },
  },
  computed: {
    alignProxy: {
      get() {
        const v = String(this.block?.settings?.align || "").toLowerCase();
        return v === "left" || v === "center" || v === "right" ? v : null;
      },
      set(next) {
        if (!this.block.settings) this.$set(this.block, "settings", {});
        const v = String(next || "").toLowerCase();
        const safe = v === "left" || v === "center" || v === "right" ? v : null;
        if (!safe) this.$delete(this.block.settings, "align");
        else this.$set(this.block.settings, "align", safe);
      },
    },
    displayProxy: {
      get() {
        const v = String(this.block?.settings?.display || "").toLowerCase();
        // Back-compat: "inline" -> "inline-block"
        return v === "inline-block" || v === "inline" ? "inline-block" : "block";
      },
      set(next) {
        const v = String(next || "").toLowerCase();
        const safe = v === "inline-block" || v === "inline" ? "inline-block" : "block";
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "display", safe);
      },
    },
    widthModeProxy: {
      get() {
        const v = String(this.block?.settings?.widthMode || "").toLowerCase();
        if (v === "px" || v === "pct") return v;
        // Default: px for inline-block
        return this.displayProxy === "inline-block" ? "px" : "pct";
      },
      set(next) {
        const v = String(next || "").toLowerCase();
        const safe = v === "px" || v === "pct" ? v : "px";
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "widthMode", safe);
      },
    },
    widthPctProxy: {
      get() {
        const n = Number(this.block?.settings?.widthPct);
        if (!Number.isFinite(n)) return 100;
        return Math.min(100, Math.max(10, Math.round(n)));
      },
      set(v) {
        const n = Number(v);
        const next = Number.isFinite(n) ? Math.min(100, Math.max(10, Math.round(n))) : 100;
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "widthPct", next);
      },
    },
    widthPxProxy: {
      get() {
        const n = Number(this.block?.settings?.widthPx);
        if (!Number.isFinite(n)) return 0;
        return Math.min(600, Math.max(0, Math.round(n)));
      },
      set(v) {
        const n = Number(v);
        const next = Number.isFinite(n) ? Math.min(600, Math.max(0, Math.round(n))) : 0;
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "widthPx", next);
      },
    },
  },
};
</script>


